import { useCallback, useEffect, useState } from "react";
import { SEED } from "../data/seed";
import { isTripData } from "../data/validateTrip";
import {
  mergeSeedPinImages,
  migrateTripData,
  scrubPrivacyFromSeed,
} from "../data/migrateTrip";
import type {
  DayPlan,
  ScheduleItem,
  TodoItem,
  TravelerId,
  TripData,
  VoteValue,
} from "../types";

export { scoreVotes, migrateTripData } from "../data/migrateTrip";

const STORAGE_KEY = "chile-trip-v11";
const LEGACY_KEYS = [
  "chile-trip-v10",
  "chile-trip-v9",
  "chile-trip-v8",
  "chile-trip-v7",
  "chile-trip-v6",
  "chile-trip-v5",
  "chile-trip-v4",
  "chile-trip-v3",
  "chile-trip-v2",
  "chile-trip-v1",
];

function load(): TripData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (isTripData(parsed))
        return scrubPrivacyFromSeed(mergeSeedPinImages(parsed));
      const migrated = migrateTripData(parsed);
      if (migrated) return migrated;
    }
    for (const key of LEGACY_KEYS) {
      const legacy = localStorage.getItem(key);
      if (!legacy) continue;
      try {
        const migrated = migrateTripData(JSON.parse(legacy));
        if (migrated) return migrated;
      } catch {
        /* ignore */
      }
    }
    return structuredClone(SEED);
  } catch {
    return structuredClone(SEED);
  }
}

function save(data: TripData) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function useTripData() {
  const [data, setData] = useState<TripData>(() => load());

  useEffect(() => {
    save(data);
  }, [data]);

  const updateDay = useCallback((date: string, patch: Partial<DayPlan>) => {
    setData((prev) => ({
      ...prev,
      days: prev.days.map((d) => (d.date === date ? { ...d, ...patch } : d)),
    }));
  }, []);

  const setTodos = useCallback(
    (date: string, todos: TodoItem[]) => {
      updateDay(date, { todos });
    },
    [updateDay],
  );

  const toggleTodo = useCallback((date: string, todoId: string) => {
    setData((prev) => ({
      ...prev,
      days: prev.days.map((d) =>
        d.date !== date
          ? d
          : {
              ...d,
              todos: d.todos.map((t) =>
                t.id === todoId ? { ...t, done: !t.done } : t,
              ),
            },
      ),
    }));
  }, []);

  const addTodo = useCallback((date: string, text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setData((prev) => ({
      ...prev,
      days: prev.days.map((d) =>
        d.date !== date
          ? d
          : {
              ...d,
              todos: [
                ...d.todos,
                {
                  id: `todo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                  text: trimmed,
                  done: false,
                },
              ],
            },
      ),
    }));
  }, []);

  const removeTodo = useCallback((date: string, todoId: string) => {
    setData((prev) => ({
      ...prev,
      days: prev.days.map((d) =>
        d.date !== date
          ? d
          : { ...d, todos: d.todos.filter((t) => t.id !== todoId) },
      ),
    }));
  }, []);

  const editTodo = useCallback((date: string, todoId: string, text: string) => {
    setData((prev) => ({
      ...prev,
      days: prev.days.map((d) =>
        d.date !== date
          ? d
          : {
              ...d,
              todos: d.todos.map((t) => (t.id === todoId ? { ...t, text } : t)),
            },
      ),
    }));
  }, []);

  const setVote = useCallback(
    (targetId: string, travelerId: TravelerId, value: VoteValue | null) => {
      setData((prev) => {
        const current = { ...(prev.votes[targetId] ?? {}) };
        if (value === null) delete current[travelerId];
        else current[travelerId] = value;
        const votes = { ...prev.votes };
        if (current.michael === undefined && current.suzy === undefined) {
          delete votes[targetId];
        } else {
          votes[targetId] = current;
        }
        return { ...prev, votes };
      });
    },
    [],
  );

  const reorderDayItems = useCallback((date: string, itemIds: string[]) => {
    setData((prev) => ({
      ...prev,
      days: prev.days.map((d) => {
        if (d.date !== date) return d;
        const byId = new Map(d.items.map((i) => [i.id, i]));
        const next: ScheduleItem[] = [];
        for (const id of itemIds) {
          const item = byId.get(id);
          if (item) {
            next.push(item);
            byId.delete(id);
          }
        }
        for (const item of byId.values()) next.push(item);
        return { ...d, items: next };
      }),
    }));
  }, []);

  const moveItem = useCallback(
    (fromDate: string, toDate: string, itemId: string, toIndex?: number) => {
      setData((prev) => {
        const fromDay = prev.days.find((d) => d.date === fromDate);
        const toDay = prev.days.find((d) => d.date === toDate);
        if (!fromDay || !toDay) return prev;
        const item = fromDay.items.find((i) => i.id === itemId);
        if (!item) return prev;
        const movable =
          (item.category === "food" ||
            item.category === "coffee" ||
            item.category === "attraction") &&
          item.status !== "confirmed";
        if (!movable) return prev;

        if (fromDate === toDate) {
          const ids = fromDay.items.map((i) => i.id);
          const fromIndex = ids.indexOf(itemId);
          if (fromIndex < 0) return prev;
          ids.splice(fromIndex, 1);
          const insertAt =
            toIndex === undefined
              ? ids.length
              : Math.max(0, Math.min(toIndex, ids.length));
          ids.splice(insertAt, 0, itemId);
          const byId = new Map(fromDay.items.map((i) => [i.id, i]));
          return {
            ...prev,
            days: prev.days.map((d) =>
              d.date === fromDate
                ? { ...d, items: ids.map((id) => byId.get(id)!) }
                : d,
            ),
          };
        }

        const fromItems = fromDay.items.filter((i) => i.id !== itemId);
        const toItems = [...toDay.items];
        const insertAt =
          toIndex === undefined
            ? toItems.length
            : Math.max(0, Math.min(toIndex, toItems.length));
        toItems.splice(insertAt, 0, item);
        return {
          ...prev,
          days: prev.days.map((d) => {
            if (d.date === fromDate) return { ...d, items: fromItems };
            if (d.date === toDate) return { ...d, items: toItems };
            return d;
          }),
        };
      });
    },
    [],
  );

  const addIdeaToDay = useCallback((ideaId: string, date: string) => {
    setData((prev) => {
      const idea = prev.ideas.find((i) => i.id === ideaId);
      const day = prev.days.find((d) => d.date === date);
      if (!idea || !day) return prev;
      if (
        day.items.some((i) => i.id === idea.id || i.id === `sched-${idea.id}`)
      ) {
        return prev;
      }
      const item: ScheduleItem = {
        id: `sched-${idea.id}`,
        title: idea.name,
        detail: idea.detail
          ? `PROPOSED · from Ideas · ${idea.detail}`
          : "PROPOSED · added from Ideas",
        category: idea.category,
        status: "proposed",
        pinId: idea.pinId,
      };
      return {
        ...prev,
        days: prev.days.map((d) =>
          d.date === date ? { ...d, items: [...d.items, item] } : d,
        ),
      };
    });
  }, []);

  /** Persist a Google Place ID onto a pin once Places enrichment resolves it. */
  const setPinPlaceId = useCallback((pinId: string, placeId: string) => {
    const id = placeId.trim().replace(/^places\//, "");
    if (!pinId || !id) return;
    setData((prev) => {
      const pin = prev.pins.find((p) => p.id === pinId);
      if (!pin || pin.placeId === id) return prev;
      return {
        ...prev,
        pins: prev.pins.map((p) =>
          p.id === pinId ? { ...p, placeId: id } : p,
        ),
      };
    });
  }, []);

  const resetToSeed = useCallback(() => {
    const fresh = structuredClone(SEED);
    setData(fresh);
    save(fresh);
  }, []);

  const exportJson = useCallback(() => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chile-trip-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  const importJson = useCallback((file: File) => {
    return new Promise<void>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(String(reader.result)) as unknown;
          const next =
            (isTripData(parsed)
              ? scrubPrivacyFromSeed(mergeSeedPinImages(parsed))
              : null) ?? migrateTripData(parsed);
          if (!next) throw new Error("Invalid trip file");
          save(next);
          setData(next);
          resolve();
        } catch (e) {
          reject(e);
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }, []);

  const getDay = useCallback(
    (date: string) => data.days.find((d) => d.date === date),
    [data.days],
  );

  return {
    data,
    setTodos,
    toggleTodo,
    addTodo,
    removeTodo,
    editTodo,
    setVote,
    reorderDayItems,
    moveItem,
    addIdeaToDay,
    setPinPlaceId,
    resetToSeed,
    exportJson,
    importJson,
    getDay,
  };
}

export type TripStore = ReturnType<typeof useTripData>;
